const PERSONALISATION_EMOJI_PATTERN = /\p{Extended_Pictographic}/gu;

/** Alphanumeric, spaces, and basic punctuation only (matches legacy theme). */
const PERSONALISATION_DISALLOWED_CHARS_PATTERN = /[^\w\s.,!?@$&£'"\-_()]/g;

const sanitizePersonalisationText = (text) =>
  text.replace(PERSONALISATION_EMOJI_PATTERN, '').replace(PERSONALISATION_DISALLOWED_CHARS_PATTERN, '');

class ProductPersonalisation extends HTMLElement {
  #dockObserver = null;
  #productRoot = null;
  #onVariantAreaChangeBound = null;
  #isOpening = false;
  #abortController = null;
  #initialized = false;
  /** @type {{ text1: string, text2: string, text3: string, font: string | null, color: string | null, icon: string | null } | null} */
  #committed = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#setup();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    if (this.modal?.hasAttribute('open')) {
      this.close({ force: true });
    }

    this.#abortController?.abort();
    this.#abortController = null;

    if (this.#productRoot && this.#onVariantAreaChangeBound) {
      this.#productRoot.removeEventListener('change', this.#onVariantAreaChangeBound);
    }

    this.#productRoot = null;
    this.#onVariantAreaChangeBound = null;
    this.#committed = null;

    if (this.#dockObserver) {
      this.#dockObserver.disconnect();
      this.#dockObserver = null;
    }

    this.#initialized = false;
    this.isActive = false;
  }

  #setup() {
    if (this.#initialized) return;

    // Element Refs
    this.container = this.querySelector('.personalization-trigger');
    this.modal = this.querySelector('.personalization-modal');
    this.modalSheet = this.querySelector('.personalization-modal__content');
    this.modalScroll = this.querySelector('.personalization-modal__scroll');
    this.closeBtn = this.querySelector('.js-pers-save');
    this.removeBtn = this.querySelector('.js-pers-remove');

    // Inputs
    this.textInput = this.querySelector('#pers-text-1');
    this.textInput2 = this.querySelector('#pers-text-2');
    this.textInput3 = this.querySelector('#pers-text-3');
    this.fontInputs = this.querySelectorAll('[name="pers-font"]');
    this.colorInputs = this.querySelectorAll('[name="pers-color"]');
    this.iconInputs = this.querySelectorAll('[name="pers-icon"]');

    const configScript = this.querySelector(':scope > script[type="application/json"]');

    if (!configScript) {
      console.error('[product-personalisation] Missing config JSON');
      return;
    }

    try {
      this.config = JSON.parse(configScript.textContent);
    } catch (error) {
      console.error('[product-personalisation] Invalid config JSON', error);
      return;
    }

    if (!this.container || !this.modal || !this.textInput || !this.closeBtn) {
      console.error('[product-personalisation] Missing required markup');
      return;
    }

    // Preview Refs
    this.previewText = this.querySelector('.js-preview-text');
    this.previewText2 = this.querySelector('.js-preview-text-2');
    this.previewText3 = this.querySelector('.js-preview-text-3');
    this.previewIcon = this.querySelector('.js-preview-icon');
    this.previewSurface = this.querySelector('.js-preview-surface');

    // State
    this.isActive = false;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.bindEvents(signal);
    this.init();
    this.#attachAddToCartGate();
    this.#initialized = true;
    this.syncAddToCartWithPersonalization();
  }

  init() {
    this.#ensureDefaultColorSelected();
    // Init preview state if returning visitor or existing selection
    this.updatePreview();
    this.validate();
  }

  bindEvents(signal) {
    // Modal Toggles
    this.editBtns = this.querySelectorAll('.js-pers-edit');
    for (const btn of this.editBtns) {
      btn.addEventListener('click', () => this.open(), { signal });
    }
    for (const el of this.querySelectorAll('.js-pers-close')) {
      el.addEventListener('click', () => this.close(), { signal });
    }

    // Save / Remove
    this.closeBtn.addEventListener('click', () => this.save(), { signal });
    if (this.removeBtn) this.removeBtn.addEventListener('click', () => this.reset(), { signal });

    // Input Listeners
    for (const input of this.#getTextInputs()) {
      input.addEventListener('input', () => this.#handleTextInput(input), { signal });
      input.addEventListener('paste', (event) => this.#handleTextPaste(event, input), { signal });
    }

    for (const i of this.fontInputs) i.addEventListener('change', () => this.updatePreview(), { signal });
    for (const i of this.colorInputs) i.addEventListener('change', () => this.updatePreview(), { signal });
    for (const i of this.iconInputs) i.addEventListener('change', () => this.updatePreview(), { signal });

    const personalizationInfo = this.querySelector('.personalization-info');
    if (personalizationInfo && this.modalScroll) {
      personalizationInfo.addEventListener('toggle', () => this.#scrollPersonalizationInfoIntoView(personalizationInfo), {
        signal,
      });
    }

    if (this.modalSheet) {
      this.#dockObserver = new MutationObserver(() => {
        if (!this.#usePersonalisationDock()) return;

        if (!this.modalSheet.classList.contains('active') && this.modal.hasAttribute('open')) {
          this.#syncDockDismissed();
        }
      });
      this.#dockObserver.observe(this.modalSheet, { attributes: true, attributeFilter: ['class'] });
    }
  }

  open() {
    if (!this.#initialized || !this.modal) return;

    const useDock = this.#usePersonalisationDock() && this.modalSheet;

    this.#isOpening = true;

    if (useDock) {
      if (window.dockManager) {
        window.dockManager.closeAll();
        window.dockManager.open(this.modalSheet);
      } else {
        this.modalSheet.classList.add('active');
        document.body.classList.add('dock-open');
      }

      this.modalSheet.style.transform = '';
    }

    this.modal.setAttribute('open', '');
    document.body.style.overflow = 'hidden';

    this.#isOpening = false;
  }

  /**
   * @param {{ force?: boolean }} [options]
   */
  close(options = {}) {
    const force = Boolean(options.force);

    if (!force && this.#committed && this.#hasUnsavedChanges()) {
      this.#restoreCommitted();
    }

    if (this.modalSheet && this.modalSheet.classList.contains('active')) {
      this.modalSheet.classList.remove('active');

      if (window.dockManager && window.dockManager.openModal === this.modalSheet) {
        window.dockManager.openModal = null;
      }

      document.body.classList.remove('dock-open');
    }

    this.modal.removeAttribute('open');
    document.body.style.overflow = '';
  }

  #usePersonalisationDock() {
    return window.matchMedia('(width <= 74.99em)').matches;
  }

  #syncDockDismissed() {
    if (this.#isOpening) return;

    if (this.#committed && this.#hasUnsavedChanges()) {
      this.#restoreCommitted();
    }

    this.modal.removeAttribute('open');
    document.body.style.overflow = '';

    if (window.dockManager && window.dockManager.openModal === this.modalSheet) {
      window.dockManager.openModal = null;
      document.body.classList.remove('dock-open');
    }
  }

  #getTextInputs() {
    const inputs = [this.textInput];

    if (this.textInput2) inputs.push(this.textInput2);
    if (this.textInput3) inputs.push(this.textInput3);

    return inputs;
  }

  #sanitizeTextInput(input) {
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? start;
    const original = input.value;
    const cleaned = sanitizePersonalisationText(original);

    if (cleaned === original) return false;

    const newStart = sanitizePersonalisationText(original.slice(0, start)).length;
    const newEnd = sanitizePersonalisationText(original.slice(0, end)).length;

    input.value = cleaned;
    input.setSelectionRange(newStart, newEnd);

    return true;
  }

  #handleTextInput(input) {
    this.#sanitizeTextInput(input);
    this.handleInput();
  }

  #handleTextPaste(event, input) {
    event.preventDefault();

    const pasted = sanitizePersonalisationText(event.clipboardData?.getData('text') ?? '');
    if (!pasted) return;

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? start;
    const before = input.value.slice(0, start);
    const after = input.value.slice(end);
    const maxLength = input.maxLength > 0 ? input.maxLength : Number.POSITIVE_INFINITY;
    const room = maxLength - before.length - after.length;
    const insertion = room > 0 ? pasted.slice(0, room) : '';
    const nextValue = before + insertion + after;

    input.value = nextValue;

    const cursor = before.length + insertion.length;
    input.setSelectionRange(cursor, cursor);
    this.#sanitizeTextInput(input);
    this.handleInput();
  }

  handleInput() {
    this.validate();
    this.updatePreview();
  }

  validate() {
    let isValid = this.textInput.value.trim().length > 0;

    if (this.config.personalizationRequired === true) {
      const line2 = this.textInput2 ? this.textInput2.value.trim() : '';
      isValid = isValid && line2.length > 0;
    }

    const needsColor =
      this.#getColorInputs().length > 0 ||
      (this.config.colors?.length ?? 0) > 0 ||
      Boolean(this.config.defaultColorName?.trim());

    if (needsColor && !this.#getResolvedColorName()) {
      isValid = false;
    }

    if (isValid) {
      this.closeBtn.removeAttribute('disabled');
    } else {
      this.closeBtn.setAttribute('disabled', 'true');
    }

    return isValid;
  }

  updatePreview() {
    // Text
    if (this.previewText) this.previewText.textContent = this.textInput.value || this.config.placeholder;
    if (this.previewText2) this.previewText2.textContent = this.textInput2.value;
    if (this.previewText3) this.previewText3.textContent = this.textInput3.value;

    // Font
    const selectedFont = this.getSelectedValue(this.fontInputs);
    if (selectedFont) {
      for (const el of [this.previewText, this.previewText2, this.previewText3]) {
        if (el) el.style.fontFamily = selectedFont;
      }
      const labelFont = this.querySelector('.js-label-font');
      if (labelFont) labelFont.textContent = selectedFont;
    }

    // Color
    const selectedColorName = this.#getResolvedColorName();
    const colorData = this.config.colors?.find((c) => c.name === selectedColorName);
    let stitchHex = null;

    if (colorData) {
      stitchHex = this.#stitchColorToCss(colorData);
      const bgImage = colorData.bgImage;

      for (const el of [this.previewText, this.previewText2, this.previewText3]) {
        if (!el) continue;
        if (bgImage) {
          el.style.backgroundImage = `url(${bgImage})`;
          el.classList.add('pers-text-img');
          el.style.setProperty('color', 'transparent', 'important');
        } else {
          el.style.backgroundImage = 'none';
          el.classList.remove('pers-text-img');
          if (stitchHex) {
            el.style.setProperty('color', stitchHex, 'important');
          } else {
            el.style.removeProperty('color');
          }
        }
      }

      const labelColor = this.querySelector('.js-label-color');
      if (labelColor) labelColor.textContent = selectedColorName;
    }

    if (this.previewSurface) {
      if (stitchHex) {
        this.previewSurface.style.setProperty('color', stitchHex, 'important');
      } else {
        this.previewSurface.style.removeProperty('color');
      }
    }

    // Icon
    const selectedIcon = this.getSelectedValue(this.iconInputs);
    const noneIcon = this.config.iconNoneValue ?? 'No Icon';

    if (this.previewIcon) {
      if (selectedIcon && selectedIcon !== noneIcon) {
        const iconSvg = this.querySelector(
          `label[for="${this.getIdForValue(this.iconInputs, selectedIcon)}"] .js-icon-source`,
        )?.innerHTML;
        this.previewIcon.innerHTML = iconSvg || '';
      } else {
        this.previewIcon.innerHTML = '';
      }
    }

    if (stitchHex) {
      this.#syncPreviewIconToStitchColor(stitchHex);
    } else if (this.previewIcon) {
      this.previewIcon.style.removeProperty('color');
    }

    const labelIcon = this.querySelector('.js-label-icon');
    if (labelIcon) {
      if (!selectedIcon || selectedIcon === noneIcon) {
        labelIcon.textContent = this.config.iconNoneLabel || 'None';
      } else {
        labelIcon.textContent = selectedIcon;
      }
    }
  }

  #sanitizeAllTextInputs() {
    for (const input of this.#getTextInputs()) {
      this.#sanitizeTextInput(input);
    }
  }

  save() {
    this.#sanitizeAllTextInputs();
    this.#ensureDefaultColorSelected();

    if (!this.validate()) return;

    this.isActive = true;
    this.#captureCommitted();
    this.close({ force: true });

    // Update UI State
    this.container.querySelector('.js-state-inactive').classList.add('hidden');
    this.container.querySelector('.js-state-active').classList.remove('hidden');

    if (this.editBtns) {
      for (const btn of this.editBtns) {
        btn.textContent = this.config.strings?.editCta ?? '';
      }
    }

    this.dispatchEvent(
      new CustomEvent('personalization:change', {
        bubbles: true,
        detail: { active: true },
      }),
    );

    this.syncAddToCartWithPersonalization();
  }

  reset() {
    this.isActive = false;
    this.#committed = null;

    // Clear Inputs
    this.textInput.value = '';
    if (this.textInput2) this.textInput2.value = '';
    if (this.textInput3) this.textInput3.value = '';

    this.close({ force: true });

    // Update UI State
    this.container.querySelector('.js-state-inactive').classList.remove('hidden');
    this.container.querySelector('.js-state-active').classList.add('hidden');

    if (this.editBtns) {
      for (const btn of this.editBtns) {
        btn.textContent = this.config.strings?.addCta ?? '';
      }
    }

    this.dispatchEvent(
      new CustomEvent('personalization:change', {
        bubbles: true,
        detail: { active: false },
      }),
    );

    this.syncAddToCartWithPersonalization();
  }

  #captureCommitted() {
    this.#committed = {
      text1: this.textInput?.value ?? '',
      text2: this.textInput2?.value ?? '',
      text3: this.textInput3?.value ?? '',
      font: this.getSelectedValue(this.fontInputs),
      color: this.#getResolvedColorName(),
      icon: this.getSelectedValue(this.iconInputs),
    };
  }

  #hasUnsavedChanges() {
    if (!this.#committed) return false;

    if ((this.textInput?.value ?? '') !== this.#committed.text1) return true;
    if ((this.textInput2?.value ?? '') !== this.#committed.text2) return true;
    if ((this.textInput3?.value ?? '') !== this.#committed.text3) return true;
    if (this.getSelectedValue(this.fontInputs) !== this.#committed.font) return true;
    if (this.#getResolvedColorName() !== this.#committed.color) return true;
    if (this.getSelectedValue(this.iconInputs) !== this.#committed.icon) return true;

    return false;
  }

  #setRadioSelection(nodeList, value) {
    if (!nodeList?.length) return;

    let matched = false;

    for (const input of nodeList) {
      const isMatch = value != null && input.value === value;
      input.checked = isMatch;
      if (isMatch) matched = true;
    }

    if (!matched && nodeList[0]) {
      nodeList[0].checked = true;
    }
  }

  #restoreCommitted() {
    if (!this.#committed) return;

    if (this.textInput) this.textInput.value = this.#committed.text1;
    if (this.textInput2) this.textInput2.value = this.#committed.text2;
    if (this.textInput3) this.textInput3.value = this.#committed.text3;

    this.#setRadioSelection(this.fontInputs, this.#committed.font);
    this.#setRadioSelection(this.#getColorInputs(), this.#committed.color);
    this.#setRadioSelection(this.iconInputs, this.#committed.icon);

    this.updatePreview();
    this.validate();
  }

  #hasRequiredPersonalizationContent() {
    const line1 = this.textInput?.value.trim() ?? '';

    if (!line1) return false;

    if (this.config.personalizationRequired === true) {
      const line2 = this.textInput2?.value.trim() ?? '';

      if (!line2) return false;
    }

    const needsColor =
      this.#getColorInputs().length > 0 ||
      (this.config.colors?.length ?? 0) > 0 ||
      Boolean(this.config.defaultColorName?.trim());

    if (needsColor && !this.#getResolvedColorName()) {
      return false;
    }

    return true;
  }

  /**
   * When three-line personalisation is required, the buyer must save personalisation before checkout path.
   */
  requiresPersonalizationBeforeCart() {
    if (!this.#initialized || !this.config) return false;
    if (this.config.personalizationRequired !== true) return false;
    if (!this.isActive) return true;

    return !this.#hasRequiredPersonalizationContent();
  }

  /**
   * Keeps the product ATC button disabled until personalisation is saved (required mode only).
   * Called after variant-driven buy-button HTML swaps.
   */
  syncAddToCartWithPersonalization() {
    if (!this.#initialized || !this.config || this.config.personalizationRequired !== true) return;

    const submit = this.#getAddToCartButton();
    if (!submit) return;

    const blockedTitle = this.config.strings?.atcBlockedTitle ?? '';
    const shouldBlock = this.requiresPersonalizationBeforeCart();

    if (shouldBlock) {
      submit.disabled = true;
      submit.dataset.disabledByPersonalization = 'true';

      if (blockedTitle) {
        submit.setAttribute('title', blockedTitle);
      }

      return;
    }

    if (submit.dataset.disabledByPersonalization === 'true') {
      delete submit.dataset.disabledByPersonalization;
      submit.removeAttribute('title');

      const variantPicker = this.#getVariantPicker();
      const soldOutLabel = variantPicker?.dataset.soldOutLabel?.trim();
      const span = submit.querySelector('span');
      const labelText = span?.textContent.trim() ?? '';

      if (soldOutLabel && labelText === soldOutLabel) {
        return;
      }

      submit.disabled = false;
    }
  }

  /**
   * Public Method called by ProductForm
   * Returns the payload required for Bundled Add
   */
  getPayload() {
    if (!this.#initialized || !this.isActive) return null;

    this.#sanitizeAllTextInputs();
    this.#ensureDefaultColorSelected();

    if (!this.#hasRequiredPersonalizationContent()) return null;

    const mainText = sanitizePersonalisationText(this.textInput.value);
    const text2 = this.textInput2 ? sanitizePersonalisationText(this.textInput2.value) : '';
    const text3 = this.textInput3 ? sanitizePersonalisationText(this.textInput3.value) : '';

    // Combine Text logic
    let combinedText = mainText;
    if (text2 || text3) combinedText += ` | ${text2} | ${text3}`;

    // Metadata
    const font = this.getSelectedValue(this.fontInputs);
    const color = this.#getResolvedColorName();
    const icon = this.getSelectedValue(this.iconInputs);
    const noneIcon = this.config.iconNoneValue ?? 'No Icon';
    const iconForSettings = icon && icon !== noneIcon ? icon : noneIcon;
    const settingsString = `Font: ${font ?? ''} | Color: ${color ?? ''} | Icon: ${iconForSettings}`;

    return {
      feeId: this.config.feeVariantId,
      properties: {
        'Personalisation Text': combinedText,
        'Personalisation Settings': settingsString,
        'For Product': this.config.productTitle,
        _unique_id: String(Date.now()),
      },
    };
  }

  // Helpers
  getSelectedValue(nodeList) {
    const item = Array.from(nodeList).find((i) => i.checked);
    return item ? item.value : null;
  }

  #getColorInputs() {
    return this.querySelectorAll('[name="pers-color"]');
  }

  #ensureDefaultColorSelected() {
    const inputs = this.#getColorInputs();
    if (!inputs.length) return;

    if (Array.from(inputs).some((input) => input.checked)) return;

    const preferred =
      this.config.defaultColorName || this.config.colors?.find((entry) => entry?.name)?.name || null;

    let target = inputs[0];

    if (preferred) {
      const match = Array.from(inputs).find((input) => input.value === preferred);
      if (match) target = match;
    }

    target.checked = true;
  }

  /**
   * Stitch colour name for cart properties — never null when config or inputs provide one.
   */
  #getResolvedColorName() {
    const fromDom = this.getSelectedValue(this.#getColorInputs());
    if (fromDom) return fromDom;

    const fromConfig =
      this.config.defaultColorName || this.config.colors?.find((entry) => entry?.name)?.name || null;

    if (fromConfig) return fromConfig;

    const fallback = this.config.fallbackColorName?.trim();
    if (fallback) return fallback;

    return null;
  }

  getIdForValue(nodeList, value) {
    const item = Array.from(nodeList).find((i) => i.value === value);
    return item ? item.id : null;
  }

  /**
   * Shopify color metaobject fields often `| json` as an object; Liquid may also emit a plain CSS string.
   * Returns a value valid for CSS `color` / `stroke`, or null.
   */
  #stitchColorToCss(colorEntry) {
    if (!colorEntry) return null;
    return this.#normalizeStitchColor(colorEntry.hex ?? colorEntry);
  }

  #normalizeStitchColor(raw) {
    if (raw == null || raw === '') return null;

    if (typeof raw === 'string') {
      const t = raw.trim();
      if (!t) return null;
      if (t.startsWith('#')) return t;
      if (/^[\dA-Fa-f]{6}$/u.test(t)) return `#${t}`;
      if (/^[\dA-Fa-f]{3}$/u.test(t)) return `#${t}`;
      if (/^rgb\(/iu.test(t) || /^hsl\(/iu.test(t)) return t;
      return t;
    }

    if (typeof raw === 'object') {
      if (typeof raw.hex === 'string') return this.#normalizeStitchColor(raw.hex);
      const r = raw.red ?? raw.r;
      const g = raw.green ?? raw.g;
      const b = raw.blue ?? raw.b;
      if (r != null && g != null && b != null) {
        const channel = (x) => {
          const n = Number(x);
          if (!Number.isFinite(n)) return 0;
          return n <= 1 ? Math.round(n * 255) : Math.round(n);
        };
        return `rgb(${channel(r)},${channel(g)},${channel(b)})`;
      }
    }

    return null;
  }

  /**
   * When the info accordion opens, gently scroll the modal so the save row stays at the top of the
   * visible area (with padding). Avoid scrolling to fully reveal long FAQ content, which hides the save.
   */
  #scrollPersonalizationInfoIntoView(details) {
    if (!details.open || !this.modalScroll || !this.closeBtn) return;

    requestAnimationFrame(() => {
      const anchor = this.closeBtn.closest('.pers-button-group') || this.closeBtn;
      const scrollRect = this.modalScroll.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      const paddingTop = 12;
      const delta = anchorRect.top - scrollRect.top - paddingTop;

      if (Math.abs(delta) < 3) return;

      this.modalScroll.scrollBy({ top: delta, behavior: 'smooth' });
    });
  }

  #syncPreviewIconToStitchColor(hex) {
    if (!this.previewIcon || !hex) return;

    this.previewIcon.style.setProperty('color', hex);

    for (const node of this.previewIcon.querySelectorAll('svg, svg *')) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;

      const stroke = node.getAttribute('stroke');
      if (stroke && stroke !== 'none' && stroke !== 'transparent' && stroke !== 'currentColor') {
        node.setAttribute('stroke', hex);
      }

      const fill = node.getAttribute('fill');
      if (fill && fill !== 'none' && fill !== 'transparent' && fill !== 'currentColor') {
        node.setAttribute('fill', hex);
      }
    }
  }

  #attachAddToCartGate() {
    if (this.config.personalizationRequired !== true) return;

    this.#productRoot = this.closest('.product');
    if (!this.#productRoot) return;

    this.#onVariantAreaChangeBound = (e) => {
      const variantPicker = this.#productRoot.querySelector('variant-picker');

      if (!variantPicker || !e.target || !variantPicker.contains(e.target)) {
        return;
      }

      requestAnimationFrame(() => this.syncAddToCartWithPersonalization());
    };

    this.#productRoot.addEventListener('change', this.#onVariantAreaChangeBound);
  }

  #getAddToCartButton() {
    return this.closest('.product')?.querySelector('product-form .product-form__submit') ?? null;
  }

  #getVariantPicker() {
    return this.closest('.product')?.querySelector('variant-picker') ?? null;
  }
}

customElements.define('product-personalisation', ProductPersonalisation);

/**
 * Re-sync personalisation after Section Rendering swaps (e.g. combined listing colour).
 */
const refreshProductPersonalisation = (root = document) => {
  for (const el of root.querySelectorAll('product-personalisation')) {
    if (!el.isConnected) continue;

    if (typeof el.syncAddToCartWithPersonalization === 'function') {
      el.syncAddToCartWithPersonalization();
    }
  }
};

window.refreshProductPersonalisation = refreshProductPersonalisation;
